import { Card, Col, Collapse, Row, Space, Tag, Typography } from "antd";
import {
  CalendarOutlined,
  CustomerServiceOutlined,
  FileProtectOutlined,
  FolderOpenOutlined,
  HeartOutlined,
  MailOutlined,
  PhoneOutlined,
  QuestionCircleOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { OLIVEEB_COLORS } from "workspaces/colors";

const { Paragraph, Text, Title } = Typography;

const systemSections = [
  {
    title: "Resident Care",
    description: "Manage resident profiles, onboarding details, contacts, daily notes, documents, and care-team context in one workspace.",
    icon: <HeartOutlined />,
  },
  {
    title: "Resident Care Processes",
    description: "Guide new and current residents through state-document review, onboarding, observations, probation, care planning, and final confirmation.",
    icon: <FileProtectOutlined />,
  },
  {
    title: "Schedules & Attendance",
    description: "Create staff schedules, check for active schedule overlaps, clock in and out, and review timesheets based on role permissions.",
    icon: <CalendarOutlined />,
  },
  {
    title: "Documents",
    description: "Organize public and internal workspace documents with folders, previews, shareable links, uploads, and downloads.",
    icon: <FolderOpenOutlined />,
  },
];

const contactItems = [
  {
    title: "Product Support",
    detail: "Use this when workflows, data, uploads, scheduling, resident care processes, or workspace setup do not behave as expected.",
    action: "support@oliveeb.com",
    icon: <CustomerServiceOutlined />,
  },
  {
    title: "Account & Access",
    detail: "Use this for invitations, workspace membership, roles, permissions, login, or account security issues.",
    action: "security@oliveeb.com",
    icon: <SafetyCertificateOutlined />,
  },
  {
    title: "Urgent Operations",
    detail: "For urgent operational questions, include the workspace name, affected user, page URL, and exact time of the issue.",
    action: "+1 (425) 770-7996",
    icon: <PhoneOutlined />,
  },
];

const faqItems = [
  {
    key: "workspace",
    label: "Why do records depend on my selected workspace?",
    children: "OCarely is workspace-based. Residents, care processes, schedules, documents, timesheets, organization locations, and subscriptions are filtered by the current workspace so staff only see records tied to their membership.",
  },
  {
    key: "roles",
    label: "Who can manage administrative features?",
    children: "Care owners and care admins can manage workspace operations such as schedules, attendance review, timesheet approvals, access control, subscriptions, organization settings, and administrative care-process actions. Care workers see their own schedule, clocking, and assigned work where allowed.",
  },
  {
    key: "documents",
    label: "How do Documents work?",
    children: "Documents are split into public documents and internal workspace documents. Internal documents can be uploaded into folders, previewed, downloaded, moved by drag and drop, and shared with bookmarkable links. Public documents are shared Oliveeb resources.",
  },
  {
    key: "assessments",
    label: "What happens when a new resident care process is completed?",
    children: "The care process follows the configured workflow. For prospective residents, completing the final assessment step can activate the resident when the resident care process is finished.",
  },
  {
    key: "schedules",
    label: "Why does the schedule form block some new schedules?",
    children: "The schedule screen checks active schedules for the same care staff member. If a new schedule overlaps an existing active schedule, the form shows a warning and blocks saving until the overlap is corrected.",
  },
  {
    key: "support",
    label: "What should I include when contacting support?",
    children: "Include the page URL, workspace name, resident or staff name if relevant, what you expected to happen, what actually happened, and the approximate time. Screenshots help when the issue is visual.",
  },
];

const Help = () => (
  <div className="project-page help-center-page">
    <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
      <WidgetSectionTitle
        icon={<QuestionCircleOutlined />}
        title="Help Center"
        subtitle="Guidance for OCarely workflows, support, and common questions"
        className="flex-1"
        titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
        subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
      />
    </div>

    <Card className="help-center-hero">
      <Space direction="vertical" size={10}>
        <Tag color="green">OCarely</Tag>
        <Title level={3}>Care facility operations and care worker tools in one workspace.</Title>
        <Paragraph>
          OCarely helps Adult Family Homes, Assisted Living Facilities, care teams, and care workers manage residents,
          assessments, negotiated care plans, schedules, attendance, daily notes, documents, hiring, billing, websites, and
          AI-assisted care workflows.
        </Paragraph>
      </Space>
    </Card>

    <section className="help-center-section">
      <Title level={4}>About the System</Title>
      <Row gutter={[16, 16]}>
        {systemSections.map((item) => (
          <Col xs={24} md={12} xl={6} key={item.title}>
            <Card className="help-center-card">
              <Space direction="vertical" size={12}>
                <span className="help-center-icon">{item.icon}</span>
                <Text strong>{item.title}</Text>
                <Paragraph type="secondary">{item.description}</Paragraph>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>
    </section>

    <section className="help-center-section">
      <Title level={4}>Contact Support</Title>
      <Row gutter={[16, 16]}>
        {contactItems.map((item) => (
          <Col xs={24} lg={8} key={item.title}>
            <Card className="help-center-contact-card">
              <Space direction="vertical" size={10}>
                <span className="help-center-icon blue">{item.icon}</span>
                <Text strong>{item.title}</Text>
                <Paragraph type="secondary">{item.detail}</Paragraph>
                <Text className="help-center-contact-value">
                  {item.action.includes("@") ? <MailOutlined /> : <PhoneOutlined />} {item.action}
                </Text>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>
    </section>

    <section className="help-center-section">
      <Title level={4}>Frequently Asked Questions</Title>
      <Collapse className="help-center-faq" items={faqItems} bordered={false} />
    </section>

    <Card className="help-center-footer-card">
      <Space align="start" size={12}>
        <span className="help-center-icon"><TeamOutlined /></span>
        <div>
          <Text strong>Need hands-on help?</Text>
          <Paragraph type="secondary">
            Send support the workspace name and the exact page URL. For care records, include only the minimum resident or staff
            details needed to locate the issue.
          </Paragraph>
        </div>
      </Space>
    </Card>
  </div>
);

export default Help;
